import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { IPackageItem, PackageTableVariant } from 'qbe-components';
import { Observable, Subject, combineLatest, map, takeUntil } from 'rxjs';
import { ModalService, ModalType } from 'src/app/services/modal.service';
import { ProcessType } from 'src/app/services/process.service';
import { AppState } from 'src/app/store';
import { formatterCurrency } from 'src/app/utils';
import * as fromConfig from '../../../store/config/config.reducer';
import * as fromProcess from '../../../store/process/process.reducer';
import isAppAgentOrTravelAgency from '../../../utils/is-agent-or-travel-agency.util';

const ZERO = 0;

interface QbePackageTableProps {
  labelComfort?: string;
  labelPlus?: string;
  labelExtra?: string;
  labelMax?: string;
  packageList: IPackageItem[];
}

@Component({
  selector: 'app-compare-offers',
  templateUrl: './compare-offers.component.html',
  styleUrls: ['./compare-offers.component.scss'],
})
export class CompareOffersComponent implements OnInit, OnDestroy {
  packages$ = combineLatest([
    this.store.select(fromProcess.selectOfferQuotationData),
    this.store.select(fromProcess.selectProcessKind),
  ]).pipe(map(([data, processKind]) => (processKind ? data?.result.path[processKind]?.packages || [] : [])));
  translations$ = this.translateService.getTranslation(this.translateService.currentLang);
  packageTableVariant = PackageTableVariant.Travel;
  owuLink: string | null = null;
  processKind$ = this.store.select(fromProcess.selectProcessKind);
  ngDestroy$ = new Subject<void>();
  proccessType = ProcessType;
  isAppAgentOrAgency = isAppAgentOrTravelAgency();

  packageTableProps$: Observable<QbePackageTableProps> = this.packages$.pipe(
    map((packages) => {
      const [comfort, plus, extra, max] = packages;
      const packageListElements = Math.max(
        comfort?.coverages?.length || ZERO,
        plus?.coverages?.length || ZERO,
        extra?.coverages?.length || ZERO,
        max?.coverages?.length || ZERO,
      );

      const packageList: IPackageItem[] = Array(packageListElements)
        .fill(null)
        .map((_, index) => {
          const coverage = comfort?.coverages?.[index];
          const coverageComfort = comfort?.coverages?.[index];
          const coveragePlus = plus?.coverages?.[index];
          const coverageExtra = extra?.coverages?.[index];
          const coverageMax = max?.coverages?.[index];

          if (!coverage) {
            throw new Error('compare-offers.component.ts: packageList.map: coverage is null');
          }

          return {
            title: coverage.name,
            comfort: coverageComfort && {
              value: coverageComfort.enabled,
              label: coverageComfort.limit ? formatterCurrency(coverageComfort.limit.amount, coverageComfort.limit.currency) : '',
            },
            plus: coveragePlus && {
              value: coveragePlus.enabled,
              label: coveragePlus.limit ? formatterCurrency(coveragePlus.limit.amount, coveragePlus.limit.currency) : '',
            },
            extra: coverageExtra && {
              value: coverageExtra.enabled,
              label: coverageExtra.limit ? formatterCurrency(coverageExtra.limit.amount, coverageExtra.limit.currency) : '',
            },
            max: coverageMax && {
              value: coverageMax.enabled,
              label: coverageMax.limit ? formatterCurrency(coverageMax.limit.amount, coverageMax.limit.currency) : '',
            },
            definition: comfort.coverages[index].additionalInformations.additionalInfo,
          };
        });

      packageList.push({
        title: '',
        comfort: comfort && {
          value: true,
          label: comfort.totalValue.amount ? formatterCurrency(comfort.totalValue.amount, comfort.totalValue.currency) : '',
        },
        plus: plus && {
          value: true,
          label: plus.totalValue.amount ? formatterCurrency(plus.totalValue.amount, plus.totalValue.currency) : '',
        },
        extra: extra && {
          value: true,
          label: extra.totalValue.amount ? formatterCurrency(extra.totalValue.amount, extra.totalValue.currency) : '',
        },
        max: max && {
          value: true,
          label: max.totalValue.amount ? formatterCurrency(max.totalValue.amount, max.totalValue.currency) : '',
        },
      });

      return {
        labelComfort: comfort?.name,
        labelPlus: plus?.name,
        labelExtra: extra?.name,
        labelMax: max?.name,
        packageList,
      };
    }),
  );

  constructor(
    private store: Store<AppState>,

    private translateService: TranslateService,
    private modalService: ModalService,
  ) {}

  ngOnInit(): void {
    combineLatest([this.store.select(fromConfig.selectConfigData), this.processKind$])
      .pipe(takeUntil(this.ngDestroy$))
      .subscribe(([configData, kind]) => {
        if (!configData) {
          return;
        }

        const configParams = configData.paths.find(({ codeName }) => codeName === kind);
        if (configParams) {
          this.owuLink = configParams.owuFile;
        }
      });
  }

  ngOnDestroy(): void {
    this.ngDestroy$.next();
    this.ngDestroy$.complete();
  }

  showOfferLeafletModal(): void {
    this.modalService.openModal(ModalType.OfferLeaflet, { maxWidth: '1472px' });
  }
}
